/**
 * Browser client for Google Maps Platform (New) HTTP APIs.
 *
 * Uses Places API (New) and Routes API (New) — both support CORS from
 * browsers with a referrer-restricted API key — so no Maps JS bundle is
 * loaded for core browsing. Strict field masks keep responses (and cost)
 * minimal. Every call is session-cached and every failure returns null:
 * Google being down, disabled, or restricted can never break a page.
 *
 * The key is injected at build time and is never logged.
 */

import { GOOGLE_MAPS_API_KEY } from './config';

const PLACES_NEARBY_URL = 'https://places.googleapis.com/v1/places:searchNearby';
const PLACES_TEXT_URL = 'https://places.googleapis.com/v1/places:searchText';
const ROUTES_URL = 'https://routes.googleapis.com/directions/v2:computeRoutes';

/** Amenity categories surfaced in the Area Intelligence module. */
export const AMENITY_CATEGORIES = [
  { key: 'grocery', types: ['supermarket', 'grocery_store'], labelKey: 'area_intel.cat_grocery', defaultLabel: 'Grocery' },
  { key: 'hospital', types: ['hospital'], labelKey: 'area_intel.cat_hospital', defaultLabel: 'Hospital' },
  { key: 'gas', types: ['gas_station'], labelKey: 'area_intel.cat_gas', defaultLabel: 'Gas Station' },
  { key: 'bank', types: ['bank', 'atm'], labelKey: 'area_intel.cat_bank', defaultLabel: 'Bank / ATM' },
  { key: 'school', types: ['school'], labelKey: 'area_intel.cat_school', defaultLabel: 'School' },
  { key: 'food', types: ['restaurant', 'cafe'], labelKey: 'area_intel.cat_food', defaultLabel: 'Restaurants & Cafés' },
];

/** Great-circle distance in km — real math on real coordinates. */
export function haversineKm(a, b) {
  if (!a || !b || !Number.isFinite(a.lat) || !Number.isFinite(b.lat)) return null;
  const rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h));
}

export function formatKm(km, unit = 'km') {
  if (!Number.isFinite(km)) return null;
  if (unit === 'mi') {
    const mi = km * 0.621371;
    return mi < 10 ? `${mi.toFixed(1)} mi` : `${Math.round(mi)} mi`;
  }
  return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
}

/* ---------------- session cache ---------------- */

function cacheGet(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
}

function cacheSet(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full/unavailable — caching is best-effort */
  }
}

function coordKey({ lat, lng }) {
  return `${lat.toFixed(4)},${lng.toFixed(4)}`;
}

/* ---------------- HTTP core ---------------- */

async function googlePost(url, body, fieldMask, apiKey) {
  if (!apiKey) return null;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': apiKey,
        'X-Goog-FieldMask': fieldMask,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/* ---------------- Places (New) ---------------- */

/**
 * Nearest places of a category. Returns [{ id, name, lat, lng, distanceKm }]
 * (closest first) or null when Google is unavailable — callers render an
 * honest unavailable state, never fabricated entries.
 */
export async function findNearby(origin, category, apiKey = GOOGLE_MAPS_API_KEY, { radiusMeters = 8000, max = 3 } = {}) {
  if (!apiKey || !origin || !Number.isFinite(origin.lat)) return null;
  const cacheId = `jc-ai:nearby:${coordKey(origin)}:${category.key}`;
  const cached = cacheGet(cacheId);
  if (cached !== undefined) return cached;

  const data = await googlePost(
    PLACES_NEARBY_URL,
    {
      includedTypes: category.types,
      maxResultCount: max,
      rankPreference: 'DISTANCE',
      locationRestriction: {
        circle: { center: { latitude: origin.lat, longitude: origin.lng }, radius: radiusMeters },
      },
    },
    'places.id,places.displayName,places.location',
    apiKey,
  );
  if (!data) {
    cacheSet(cacheId, null);
    return null;
  }
  const places = (data.places || [])
    .map((p) => {
      const lat = p.location?.latitude;
      const lng = p.location?.longitude;
      return {
        id: p.id,
        name: p.displayName?.text || null,
        lat,
        lng,
        distanceKm: haversineKm(origin, { lat, lng }),
      };
    })
    .filter((p) => p.name);
  cacheSet(cacheId, places);
  return places;
}

/** Resolve coordinates for a named place (e.g. a military base) via text search. */
export async function resolvePlaceCoords(name, near, apiKey = GOOGLE_MAPS_API_KEY) {
  if (!apiKey || !name) return null;
  const cacheId = `jc-ai:coords:${name}:${near ? coordKey(near) : 'x'}`;
  const cached = cacheGet(cacheId);
  if (cached !== undefined) return cached;

  const body = { textQuery: name, maxResultCount: 1 };
  if (near && Number.isFinite(near.lat)) {
    body.locationBias = {
      circle: { center: { latitude: near.lat, longitude: near.lng }, radius: 50000 },
    };
  }
  const data = await googlePost(PLACES_TEXT_URL, body, 'places.location,places.displayName', apiKey);
  const loc = data?.places?.[0]?.location;
  const result = loc ? { lat: loc.latitude, lng: loc.longitude } : null;
  cacheSet(cacheId, result);
  return result;
}

/* ---------------- Routes (New) ---------------- */

/** Driving route: { durationText, distanceKm } or null. */
export async function computeDriveRoute(origin, destination, apiKey = GOOGLE_MAPS_API_KEY) {
  if (!apiKey || !origin || !destination || !Number.isFinite(destination.lat)) return null;
  const cacheId = `jc-ai:route:${coordKey(origin)}:${coordKey(destination)}`;
  const cached = cacheGet(cacheId);
  if (cached !== undefined) return cached;

  const data = await googlePost(
    ROUTES_URL,
    {
      origin: { location: { latLng: { latitude: origin.lat, longitude: origin.lng } } },
      destination: { location: { latLng: { latitude: destination.lat, longitude: destination.lng } } },
      travelMode: 'DRIVE',
    },
    'routes.duration,routes.distanceMeters',
    apiKey,
  );
  const route = data?.routes?.[0];
  if (!route?.duration) {
    cacheSet(cacheId, null);
    return null;
  }
  const seconds = parseInt(String(route.duration).replace('s', ''), 10);
  const result = {
    durationText: Number.isFinite(seconds) ? `${Math.max(1, Math.round(seconds / 60))} min` : null,
    distanceKm: Number.isFinite(route.distanceMeters) ? route.distanceMeters / 1000 : null,
  };
  cacheSet(cacheId, result);
  return result;
}
